import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Tooltip, TooltipRefProps } from 'react-tooltip'
import { Assets } from '@nexds/web'
import styled, { useTheme } from 'styled-components'

import { registerInfoTooltip } from './infoTooltipController'
import {
  infoTooltipAttrs,
  infoTooltipPills,
  InfoTooltipType,
  tooltipContentsWithIcons,
  tooltipIds
} from './tooltipUtils'

function CustomTooltipPresenter() {
  const theme = useTheme()

  return (
    <>
      {/* {Object.values(tooltipIds.primary).map((id) => (
        <PrimaryTooltip
          key={id}
          id={id}
          noArrow
          delayHide={0}
          delayShow={300}
          offset={5}
          closeEvents={{ click: true }}
          render={(content) => {
            const tooltipAttributes = tooltipContentsWithIcons(theme)[id]
            if (!tooltipAttributes) return content.content
            const { icon, color } = tooltipAttributes

            const Icon = Assets[icon as keyof typeof Assets]

            return (
              <CustomTooltipContent>
                <Icon color={color} size={14} /> <span>{content.content}</span>
              </CustomTooltipContent>
            )
          }}
        />
      ))} */}
      {Object.values(tooltipIds.secondary).map((id) => (
        <SecondaryTooltip
          key={id}
          id={id}
          noArrow
          delayHide={0}
          delayShow={300}
          offset={5}
          closeEvents={{ click: true, blur: true, dblclick: true, mouseleave: true, mouseout: true, mouseup: true }}
          render={(content) => {
            const tooltipAttributes = tooltipContentsWithIcons(theme)[id]
            if (!tooltipAttributes) return content.content
            const { icon, color } = tooltipAttributes

            const Icon = Assets[icon as keyof typeof Assets]

            return (
              <CustomTooltipContent>
                <Icon color={color} size={14} /> <span>{content.content}</span>
              </CustomTooltipContent>
            )
          }}
        />
      ))}
      {Object.values(tooltipIds.info).map((id) => (
        <InfoTooltipInstance key={id} id={id} />
      ))}
    </>
  )
}

/**
 * Uma instância da tooltip de apoio ("i"): título + pílula de origem + descrição.
 *
 * As props de objeto/função PRECISAM ser estáveis: o react-tooltip as usa como dependência dos seus
 * efeitos, e literais inline fazem os efeitos (incluindo o MutationObserver que descobre âncoras)
 * re-rodarem a cada render.
 */
function InfoTooltipInstance({ id }: { id: string }) {
  const tooltipRef = useRef<TooltipRefProps>(null)

  /**
   * O hover NÃO é registrado aqui: o `delayShow` do react-tooltip é único para a instância, então
   * ligar `mouseenter` faria o clique herdar o mesmo atraso. O `InfoTooltipButton` controla o
   * atraso do hover por conta própria (`HOVER_OPEN_DELAY`) e abre a tooltip pelo mesmo caminho
   * do clique.
   */
  const openEvents = useMemo(() => ({ click: true, mouseenter: false, focus: false, mouseover: false }), [])
  const closeEvents = useMemo(() => ({ click: true }), [])
  const globalCloseEvents = useMemo(() => ({ escape: true, clickOutsideAnchor: true, scroll: true, resize: true }), [])

  /** Publica a instância para o `InfoTooltipButton` (ver `infoTooltipController`). */
  useEffect(() => registerInfoTooltip(tooltipRef), [])

  /**
   * O `clickOutsideAnchor` do react-tooltip escuta o clique em `window`, então ele não fecha a
   * tooltip quando o clique acontece dentro de um container que interrompe a propagação — é o caso
   * dos formulários de estação de P&B (`onClick={(e) => e.stopPropagation()}` no container do modal,
   * necessário para o clique dentro do form não fechar o próprio form).
   *
   * Escutar na fase de captura resolve: a captura acontece antes do alvo, então nenhum
   * `stopPropagation` do caminho de borbulha impede o fechamento.
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const tooltip = tooltipRef.current
      if (!tooltip?.isOpen) return

      const target = event.target as Node | null
      if (!target) return

      // clique dentro da própria tooltip (ela é `clickable`) ou na âncora que a abriu (que já
      // alterna sozinha) não deve fechar aqui
      if (document.getElementById(id)?.contains(target)) return
      if (tooltip.activeAnchor?.contains(target)) return

      tooltip.close()
    }

    document.addEventListener('click', handleClickOutside, true)

    return () => document.removeEventListener('click', handleClickOutside, true)
  }, [id])

  const render = useCallback(({ activeAnchor }: { activeAnchor: HTMLElement | null }) => {
    if (!activeAnchor) return null

    const title = activeAnchor.getAttribute(infoTooltipAttrs.title)
    const type = activeAnchor.getAttribute(infoTooltipAttrs.type) as InfoTooltipType | null
    const description = activeAnchor.getAttribute(infoTooltipAttrs.description)
    const pill = type ? infoTooltipPills[type] : null

    return (
      <InfoTooltipContent>
        <InfoTooltipHeader>
          {title}
          {pill && (
            <InfoTooltipPill $background={pill.background} $labelColor={pill.labelColor}>
              {pill.label}
            </InfoTooltipPill>
          )}
        </InfoTooltipHeader>
        <InfoTooltipDescription>{description}</InfoTooltipDescription>
      </InfoTooltipContent>
    )
  }, [])

  return (
    <InfoTooltip
      ref={tooltipRef}
      id={id}
      noArrow
      clickable
      delayShow={0}
      delayHide={0}
      offset={8}
      openEvents={openEvents}
      closeEvents={closeEvents}
      globalCloseEvents={globalCloseEvents}
      render={render}
    />
  )
}

// export const PrimaryTooltip = styled(Tooltip).attrs(({ theme }) => ({
//   opacity: 1,
//   style: {
//     height: 'fit-content',
//     width: 'fit-content',
//     borderRadius: '4px',
//     padding: '8px 12px',
//     backgroundColor: theme.colors.primary,
//     boxShadow: `1px 1px 1px ${theme.colors.primaryD1}`,
//     color: theme.colors.neutralL5,
//     ...theme.fonts.infoSM
//   }
// }))`
//   & {
//     z-index: 9999 !important;
//   }
// `

export const SecondaryTooltip = styled(Tooltip).attrs(({ theme }) => ({
  opacity: 1,
  style: {
    height: 'fit-content',
    width: 'fit-content',
    maxWidth: 250,
    borderRadius: '4px',
    padding: '0px 4px',
    backgroundColor: theme.colors.secondaryL1,
    boxShadow: `1px 1px 1px ${theme.colors.secondary}`,
    color: theme.colors.neutralL5,
    ...theme.fonts.infoSM
  }
}))`
  & {
    z-index: 9999 !important;
  }
`

export const InfoTooltip = styled(Tooltip).attrs(({ theme }) => ({
  opacity: 1,
  style: {
    height: 'fit-content',
    width: 'fit-content',
    maxWidth: 320,
    borderRadius: '8px',
    padding: '10px 6px',
    backgroundColor: theme.colors.secondaryL1,
    boxShadow: `1px 1px 4px ${theme.colors.secondaryD2}`,
    color: theme.colors.neutralL5,
    textAlign: 'left' as const
  }
}))`
  & {
    z-index: 9999 !important;
  }
`

export const InfoTooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

/** Espelha a métrica da tag do briefing (docs/Copy_i_Peso_Balanceamento_FINAL_1.html). */
export const InfoTooltipPill = styled.span<{ $background: string; $labelColor: string }>`
  display: inline-flex;
  align-items: center;

  padding: 3px 8px;
  border-radius: 999px;

  background-color: ${({ $background }) => $background};
  color: ${({ $labelColor }) => $labelColor};

  ${({ theme }) => theme.fonts.labelSM}
  text-transform: uppercase;
  white-space: nowrap;
`

export const InfoTooltipDescription = styled.p`
  ${({ theme }) => theme.fonts.infoSM}
  color: ${({ theme }) => theme.colors.neutralL4};
  margin: 0;
  white-space: normal;
`

export const InfoTooltipHeader = styled.div`
  ${({ theme }) => theme.fonts.titleSM}
  color: ${({ theme }) => theme.colors.neutralL5};

  ${InfoTooltipPill} {
    margin-left: 8px;
    vertical-align: middle;
  }
`

export const CustomTooltipContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`

export { CustomTooltipPresenter }
